import { useCallback, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  Modal,
  ModalContent,
  ModalFooter,
  Select,
  TextInput
} from "@dealer-center/core-components";
import { SaveAndCancel } from "@dealer-center/core-custom-components";
import {
  ACCOUNT_TYPE_OPTIONS,
  type CreateAccountFormData,
  type CreateAccountFormErrors,
  DEFAULT_CREATE_ACCOUNT_FORM,
  VERTICAL_TYPE_OPTIONS,
  validateCreateAccountForm
} from "@dealer-center/ddc-pages-account-search-interface";
import { useCreateAccount } from "@dealer-center/ddc-pages-account-search-fetch";

interface CreateAccountModalProps {
  show: boolean;
  close: () => void;
}

export const CreateAccountModal = ({
  show,
  close
}: CreateAccountModalProps) => {
  const [formData, setFormData] = useState<CreateAccountFormData>(
    DEFAULT_CREATE_ACCOUNT_FORM
  );
  const [errors, setErrors] = useState<CreateAccountFormErrors>({});
  const [createdAccountId, setCreatedAccountId] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const { mutateAsync: createAccount, isPending } = useCreateAccount();

  const isFormDisabled = !!createdAccountId || isPending;

  const updateField = useCallback(
    <K extends keyof CreateAccountFormData>(
      field: K,
      value: CreateAccountFormData[K]
    ) => {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
      setErrors(prev => {
        if (prev[field]) {
          const { [field]: _, ...rest } = prev;
          return rest;
        }
        return prev;
      });
    },
    []
  );

  const handleSubmit = useCallback(async () => {
    setHasAttemptedSubmit(true);
    const validationErrors = validateCreateAccountForm(formData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setCreateError(null);
    try {
      const response = await createAccount(formData);
      setCreatedAccountId(response.id);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create account";
      setCreateError(errorMessage);
    }
  }, [formData, createAccount]);

  const handleCancel = useCallback(() => {
    close();
  }, [close]);

  return (
    <Modal
      header="Create Account"
      show={show}
      size="large"
      onHide={handleCancel}
    >
      <ModalContent>
        <Grid container spacing={2}>
          {createdAccountId && (
            <Grid size={{ xs: 12 }}>
              <Alert disableSuccessFade role="alert" type="success">
                <Box sx={{ fontSize: "16px", fontWeight: "bold" }}>
                  Account created successfully!
                </Box>
                <Box sx={{ marginTop: "8px", fontSize: "14px" }}>
                  Account ID:{" "}
                  <strong style={{ fontSize: "16px" }}>
                    {createdAccountId}
                  </strong>
                </Box>
                <Box sx={{ marginTop: "8px", fontSize: "14px" }}>
                  Please save Account ID before closing this window.
                </Box>
              </Alert>
            </Grid>
          )}
          {createError && (
            <Grid size={{ xs: 12 }}>
              <Alert role="alert" type="error">
                {createError}
              </Alert>
            </Grid>
          )}
          {!createdAccountId && (
            <Grid size={{ xs: 12 }}>
              <Alert type="info">
                The Account ID will be generated based on the Company Name and
                Address. Fields marked with * are required.
              </Alert>
            </Grid>
          )}

          <Grid size={{ xs: 12 }}>
            <TextInput
              disabled={isFormDisabled}
              id="create-account-company-name-input"
              {...(hasAttemptedSubmit &&
                errors.companyName && {
                  errorMessage: errors.companyName,
                  hasError: true
                })}
              label="Company Name *"
              name="companyName"
              placeholder="Enter company name"
              value={formData.companyName}
              onChange={e =>
                e.target.value !== formData.companyName &&
                updateField("companyName", e.target.value)
              }
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Select
              disabled={isFormDisabled}
              displayDeselectOption={false}
              id="create-account-account-type-select"
              {...(hasAttemptedSubmit &&
                errors.accountType && {
                  errorMessage: errors.accountType,
                  hasError: true
                })}
              label="Type of Account *"
              options={ACCOUNT_TYPE_OPTIONS}
              value={formData.accountType}
              onChange={e =>
                e.target.value !== formData.accountType &&
                updateField(
                  "accountType",
                  e.target.value as CreateAccountFormData["accountType"]
                )
              }
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Select
              disabled={isFormDisabled}
              displayDeselectOption={false}
              id="create-account-vertical-type-select"
              {...(hasAttemptedSubmit &&
                errors.verticalType && {
                  errorMessage: errors.verticalType,
                  hasError: true
                })}
              label="Vertical Type *"
              options={VERTICAL_TYPE_OPTIONS}
              value={formData.verticalType}
              onChange={e =>
                e.target.value !== formData.verticalType &&
                updateField(
                  "verticalType",
                  e.target.value as CreateAccountFormData["verticalType"]
                )
              }
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextInput
              disabled={isFormDisabled}
              id="create-account-address1-input"
              {...(hasAttemptedSubmit &&
                errors.address1 && {
                  errorMessage: errors.address1,
                  hasError: true
                })}
              label="Address 1 *"
              name="address1"
              placeholder="Street address"
              value={formData.address1 || ""}
              onChange={e =>
                e.target.value !== formData.address1 &&
                updateField("address1", e.target.value)
              }
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextInput
              disabled={isFormDisabled}
              id="create-account-address2-input"
              {...(hasAttemptedSubmit &&
                errors.address2 && {
                  errorMessage: errors.address2,
                  hasError: true
                })}
              label="Address 2"
              name="address2"
              placeholder={
                isFormDisabled && !formData.address2
                  ? undefined
                  : "Apartment, suite, etc. (optional)"
              }
              value={formData.address2 || ""}
              onChange={e =>
                e.target.value !== formData.address2 &&
                updateField("address2", e.target.value)
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextInput
              disabled={isFormDisabled}
              id="create-account-city-input"
              {...(hasAttemptedSubmit &&
                errors.city && {
                  errorMessage: errors.city,
                  hasError: true
                })}
              label="City *"
              name="city"
              placeholder="City"
              value={formData.city || ""}
              onChange={e =>
                e.target.value !== formData.city &&
                updateField("city", e.target.value)
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextInput
              disabled={isFormDisabled}
              id="create-account-state-input"
              {...(hasAttemptedSubmit &&
                errors.state && {
                  errorMessage: errors.state,
                  hasError: true
                })}
              label="State *"
              name="state"
              placeholder="State"
              value={formData.state || ""}
              onChange={e => {
                const value = e.target.value.toUpperCase();
                if (value === formData.state) return;
                updateField("state", value);
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextInput
              disabled={isFormDisabled}
              id="create-account-country-input"
              {...(hasAttemptedSubmit &&
                errors.country && {
                  errorMessage: errors.country,
                  hasError: true
                })}
              label="Country *"
              name="country"
              placeholder={
                isFormDisabled && !formData.country ? undefined : "Country"
              }
              value={formData.country || ""}
              onChange={e => {
                const value = e.target.value.toUpperCase();
                if (value === formData.country) return;
                updateField("country", value);
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextInput
              disabled={isFormDisabled}
              id="create-account-zip-code-input"
              {...(hasAttemptedSubmit &&
                errors.zipCode && {
                  errorMessage: errors.zipCode,
                  hasError: true
                })}
              label="Zip Code *"
              name="zipCode"
              placeholder="Zip code"
              value={formData.zipCode || ""}
              onChange={e =>
                e.target.value !== formData.zipCode &&
                updateField("zipCode", e.target.value)
              }
            />
          </Grid>
        </Grid>
      </ModalContent>
      <ModalFooter>
        {createdAccountId ? (
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}
          >
            <Button
              analyticsConfiguration={{
                eventName: "Close Create Account Success"
              }}
              onClick={handleCancel}
            >
              Close
            </Button>
          </Box>
        ) : (
          <SaveAndCancel
            analyticsEventName="Create Account"
            isDisabled={{ save: isPending }}
            isSaving={isPending}
            saveButtonText={isPending ? "Creating..." : "Create Account"}
            onCancel={handleCancel}
            onSave={handleSubmit}
          />
        )}
      </ModalFooter>
    </Modal>
  );
};
